import { ObjectDiffView } from "@/components/ui/ObjectDiffView";
import type { ProjectAuditLog } from "@/features/audit-logs/hooks/useProjectAuditLogs";
import { useProjectAuditLogs } from "@/features/audit-logs/hooks/useProjectAuditLogs";
import { useUsers } from "@/features/users/hooks/useUsers";
import { formatDateTimeShort } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { Check, ChevronDown, ChevronRight, Copy, Search } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

export const Route = createFileRoute("/dashboard/admin/projects/$projectId/debug_audit_logs/")({
  component: DebugAuditLogs,
});

const opBadge: Record<string, string> = {
  created: "bg-green-700 text-green-200",
  updated: "bg-yellow-700 text-yellow-200",
  deleted: "bg-red-700 text-red-200",
};

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [text]);
  return (
    <button
      onClick={copy}
      className="flex items-center gap-1 rounded bg-gray-700 px-2 py-1 text-xs text-gray-300 hover:bg-gray-600"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {copied ? "Copied" : "Copy JSON"}
    </button>
  );
}

function LogEntry({
  log,
  getUserName,
}: {
  log: ProjectAuditLog;
  getUserName: (id: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  const hasDiffs = log.before || log.after;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-800"
      >
        {expanded ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />
        )}
        <span
          className={`rounded px-2 py-0.5 font-mono text-xs font-semibold ${opBadge[log.operation] ?? "bg-gray-700 text-gray-300"}`}
        >
          {log.operation.toUpperCase()}
        </span>
        <span className="font-mono text-xs text-gray-400">{log.id}</span>
        <span className="ml-auto text-xs text-gray-500">
          {formatDateTimeShort(log.timestamp)} · {getUserName(log.performedBy.authId)}
        </span>
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-gray-700 px-4 py-3">
          {/* Metadata */}
          <div className="grid grid-cols-2 gap-2 font-mono text-xs md:grid-cols-4">
            <div>
              <span className="text-gray-500">doc id: </span>
              <span className="text-gray-300">{log.id}</span>
            </div>
            <div>
              <span className="text-gray-500">project: </span>
              <span className="text-gray-300">{log.projectId}</span>
            </div>
            <div>
              <span className="text-gray-500">by: </span>
              <span className="text-gray-300">
                {log.performedBy.authId} ({log.performedBy.authType})
              </span>
            </div>
            <div>
              <span className="text-gray-500">ts: </span>
              <span className="text-gray-300">
                {log.timestamp?.toDate?.()?.toISOString?.() ?? "N/A"}
              </span>
            </div>
          </div>

          {/* Field-level changes */}
          {log.changes.length > 0 && (
            <div>
              <h4 className="mb-2 text-xs font-semibold text-gray-400 uppercase">
                Field Changes ({log.changes.length})
              </h4>
              <div className="space-y-1">
                {log.changes.map((c, i) => {
                  const bothObjects =
                    c.changeType === "modified" &&
                    c.oldValue != null &&
                    typeof c.oldValue === "object" &&
                    c.newValue != null &&
                    typeof c.newValue === "object";

                  return (
                    <div key={i} className="rounded bg-gray-800 px-3 py-2 font-mono text-xs">
                      <span className="text-purple-400">{c.field}</span>
                      <span className="text-gray-500"> [{c.changeType}]</span>
                      {bothObjects ? (
                        <div className="mt-1 ml-2 border-l border-gray-700 pl-2">
                          <ObjectDiffView
                            oldValue={c.oldValue}
                            newValue={c.newValue}
                            fieldName={c.field}
                            theme="dark"
                            defaultCollapseUnchanged
                            maxAutoExpandDepth={3}
                          />
                        </div>
                      ) : (
                        <div className="mt-1 flex flex-col gap-0.5">
                          {c.changeType !== "added" && (
                            <div className="text-red-400">
                              - {JSON.stringify(c.oldValue, null, 2)}
                            </div>
                          )}
                          {c.changeType !== "removed" && (
                            <div className="text-green-400">
                              + {JSON.stringify(c.newValue, null, 2)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Before/After diff */}
          {hasDiffs && (
            <div>
              <h4 className="mb-2 text-xs font-semibold text-gray-400 uppercase">
                Document Diff (before → after)
              </h4>
              <div className="overflow-x-auto rounded bg-gray-800 p-3 text-xs leading-5">
                <ObjectDiffView
                  oldValue={log.before ?? {}}
                  newValue={log.after ?? {}}
                  theme="dark"
                  defaultCollapseUnchanged
                  maxAutoExpandDepth={1}
                />
              </div>
            </div>
          )}

          {/* Raw JSON + Copy */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRaw(!showRaw)}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              {showRaw ? "Hide" : "Show"} raw JSON
            </button>
            <CopyButton text={JSON.stringify(log, null, 2)} />
          </div>
          {showRaw && (
            <pre className="max-h-96 overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">
              {JSON.stringify(log, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function DebugAuditLogs() {
  const { projectId } = Route.useParams();
  const { auditLogs, loading } = useProjectAuditLogs(projectId);
  const { users } = useUsers();
  const [search, setSearch] = useState("");
  const [opFilter, setOpFilter] = useState("");

  const getUserName = useCallback(
    (authId: string) => {
      if (authId === "unknown" || !authId) return "System";
      const user = users.find((u) => u.uid === authId);
      return user?.name || user?.email || authId;
    },
    [users],
  );

  const filtered = useMemo(() => {
    let logs = auditLogs;
    if (opFilter) {
      logs = logs.filter((l) => l.operation === opFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      logs = logs.filter((l) => JSON.stringify(l).toLowerCase().includes(q));
    }
    return logs;
  }, [auditLogs, search, opFilter]);

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <h1 className="font-mono text-lg font-bold text-gray-900">
          Debug Audit Logs
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({filtered.length}/{auditLogs.length})
          </span>
        </h1>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search raw JSON..."
            className="w-full rounded border border-gray-300 bg-white py-1.5 pr-3 pl-8 text-sm focus:border-purple-500 focus:outline-none"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-purple-500 focus:outline-none"
          value={opFilter}
          onChange={(e) => setOpFilter(e.target.value)}
        >
          <option value="">All operations</option>
          <option value="created">Created</option>
          <option value="updated">Updated</option>
          <option value="deleted">Deleted</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-purple-600" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500">No audit logs found.</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((log) => (
            <LogEntry key={log.id} log={log} getUserName={getUserName} />
          ))}
        </div>
      )}
    </div>
  );
}
